import { useDisclosure } from '@chakra-ui/hooks';
import DeleteModal from 'components/actions/Delete';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { archiveCustomer } from 'interfaces/customer';
import { getDeliveryTours } from 'interfaces/deliveryTour';
import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import {
    isFormErrored,
    isValidEmail,
    isValidPhoneNumber,
} from 'utils/verification';
import SaveStatus from 'components/actions/SaveStatus';
import GoBack from 'components/actions/GoBack';
import env from 'variables/config';
import axios from 'axios';
import { getCompanyAccounts } from 'interfaces/user';
import { getSupplier, updateSupplier } from 'interfaces/supplier';

const EditInformationSupplier = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [customer, setCustomer] = useState<any>(null);
    const [deliveryTours, setDeliveryTours] = useState<any>([]);
    const [isBillingAddress, setIsBillingAddress] = useState(false);
    const [saveStatus, setSaveStatus] = useState<any>('none');
    const [user, setUser] = useState(null);
    const [accounts, setAccounts] = useState([]);

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        axios
            .get(`${env.apiUrl}/user/me`, config)
            .then((res: any) => {
                const user = res.data;
                setUser(user);
            })
            .catch((err: any) => {
                console.log(err);
            });
    };

    const handleChange = (e: any) => {
        setSaveStatus('saving');
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const handleChangeBilling = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            billingAddress: e.target.value,
            deliveryAddress: e.target.value,
        }));
    };

    const initCustomer = async (id: string) => {
        const res = await getSupplier(id);
        if (res) {
            if (res.billingAddress) setIsBillingAddress(true);
            setCustomer(res);
            return;
        }
        navigate('/suppliers');
    };
    const initDeliveryTours = async () => {
        const res = await getDeliveryTours(false);
        if (res) {
            setDeliveryTours(res.data);
            return;
        }
    };

    const initAccounts = async () => {
        const res = await getCompanyAccounts();
        if (res) {
            setAccounts(res.data);
        }
    };

    const editCustomer = async () => {
        const validationRules: any = {
            mail: isValidEmail,
            phone: (value: any) => !value || isValidPhoneNumber(value),
            company: (value: any) => !!value.trim(),
        };

        if (isFormErrored(customer, validationRules)) {
            setSaveStatus('error');
            return;
        }

        const data = {
            _id: customer._id,
            company: customer.company,
            mail: customer.mail,
            orderEmail: customer.orderEmail,
            phone: customer.phone,
            orderPhone: customer.orderPhone,
            contact: customer.contact,
            notes: customer.notes,
            address: customer.address,
            deliveryFees: customer.deliveryFees,
            deliveryTime: customer.deliveryTime,
            francoPort: customer.francoPort,
        };
        const res = await updateSupplier(data);
        if (res.data) setSaveStatus('saved');
        else setSaveStatus('error');
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            editCustomer();
        }, 2000);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initCustomer(params.id);
        initDeliveryTours();
        initUser();
        initAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="mt-1 col-span-12">
                <GoBack text="Fiche fournisseur" />
            </div>
            <div className="mt-2 grid grid-cols-12 gap-3">
                <DeleteModal
                    isOpen={isOpen}
                    onClose={onClose}
                    description="Êtes-vous sûr de vouloir archiver ce client ?"
                    context="un client"
                    archived={true}
                    deleteFunction={() => {
                        archiveCustomer(params.id);
                        navigate('/customers');
                    }}
                />
                <div className={`col-span-12`}>
                    <Card extra={'w-full !p-5 border'}>
                        {/* Header */}
                        <div className="w-full px-[8px]">
                            <div className="grid grid-cols-4 gap-2">
                                <h4 className="col-span-1 sm:col-span-2 text-lg font-medium text-navy-700 dark:text-white">
                                    {customer
                                        ? customer.company
                                        : 'Edition client'}
                                </h4>
                                <div className="col-span-2 col-start-10">
                                    <button
                                        className="linear flex cursor-pointer items-center justify-center rounded-xl border border-red-500 px-2 py-2 text-xs text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                        onClick={() => onOpen()}
                                    >
                                        Archiver{' '}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* inputs */}
                        <div className="grid gap-3 grid-cols-12">
                            <div className="mt-2 flex col-span-12 justify-between">
                                <p className="text-base">
                                    Informations fournisseurs
                                </p>
                                <SaveStatus status={saveStatus} />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Raison sociale*"
                                    placeholder="Toola"
                                    value={customer?.company}
                                    id="company"
                                    type="text"
                                    onChange={handleChange}
                                    error="Raison sociale invalide"
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Email de contact*"
                                    placeholder="mathieu@toola.eu"
                                    error={'Champ requis'}
                                    isInvalid={!isValidEmail(customer?.mail)}
                                    value={customer?.mail || ''}
                                    id="mail"
                                    type="email"
                                    onChange={handleChange}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Email de commande*"
                                    placeholder="mathieu@toola.eu"
                                    error={'Champ requis'}
                                    isInvalid={!isValidEmail(customer?.mail)}
                                    value={customer?.orderEmail || ''}
                                    id="orderEmail"
                                    type="email"
                                    onChange={handleChange}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Téléphone de contact"
                                    placeholder="Téléphone"
                                    error={'invalide'}
                                    isInvalid={
                                        !isValidPhoneNumber(customer?.phone)
                                    }
                                    value={customer?.phone}
                                    id="phone"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Téléphone de commande"
                                    placeholder="Téléphone"
                                    error={'invalide'}
                                    isInvalid={
                                        !isValidPhoneNumber(
                                            customer?.orderPhone,
                                        )
                                    }
                                    value={customer?.orderPhone}
                                    id="orderPhone"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Contact`}
                                    placeholder="Contact"
                                    value={customer?.contact}
                                    id="contact"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-8 grid">
                                <InputField
                                    label={`Notes utiles`}
                                    placeholder="Notes utiles"
                                    value={customer?.notes}
                                    id="notes"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                        <div className="mt-5 grid gap-3 grid-cols-12">
                            <p className="col-span-12 text-base">
                                Autres informations
                            </p>
                            <div className="col-span-12 lg:col-span-6">
                                <InputField
                                    label="Adresse"
                                    placeholder={`Champ de Mars, 5 Av. Anatole France, 75007 Paris, France`}
                                    value={customer?.address || ''}
                                    id="adress"
                                    type="text"
                                    onChange={handleChangeBilling}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3 grid">
                                <InputField
                                    label={`Franco de port`}
                                    placeholder="Franco de port"
                                    value={customer?.francoPort}
                                    id="francoPort"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3 grid">
                                <InputField
                                    label={`Frais de livraison`}
                                    placeholder="Frais de livraison"
                                    value={customer?.deliveryFees}
                                    id="deliveryFees"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3 grid">
                                <InputField
                                    label={`Délais de livraison`}
                                    placeholder="Délais de livraison"
                                    value={customer?.deliveryTime}
                                    id="deliveryTime"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                        {saveStatus === 'saved' && (
                            <div className="mt-5">
                                <GoBack text="Retour à la fiche fournisseur" />
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </>
    );
};

export default EditInformationSupplier;
